/**
 * Dígitos Directos e Inversos — replica DigitosDirectosInversosTest.aspx.cs.
 *
 * Mecánica:
 * - Mostrar los dígitos de la secuencia uno a uno (tiempo_mili ms c/u).
 * - El usuario hace clic en botones 0-9 para reproducir la secuencia.
 * - Si OrdenInverso=true, la respuesta correcta es la secuencia al revés.
 * - CORRECTO → siguiente secuencia. INCORRECTO → fin del test.
 * - Resultado: cantidad de secuencias correctas.
 */
import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getTestConfig, TYPE_TO_API_PATH } from '../../api/client';
import TestLayout, {
  DoneScreen, ContinueButton, LoadingScreen, ErrorScreen, useSpeedMultiplier,
} from './shared/TestLayout';
import SaveResults from './shared/SaveResults';
import { navigateAfterTest } from '../../api/ensayosClient';
import SafeHtml from './shared/SafeHtml';
import { registrarUso } from '../../api/usoClient';

const PHASE = { LOADING: 'loading', INSTRUCTIONS: 'instructions', SHOWING: 'showing', BLANK: 'blank', RESPONSE: 'response', DONE: 'done' };
const BLANK_DURATION = 500;

// Valor puede venir con HTML del editor enriquecido del admin (ej. negrita); para dígitos solo importa el texto.
function plainValor(valor) {
  if (!valor) return '';
  return new DOMParser().parseFromString(valor, 'text/html').body.textContent || '';
}

function parseDigits(valor) {
  return plainValor(valor).split(';').map((d) => d.trim()).filter(Boolean);
}

export default function TestRunDigitos() {
  const { lenguajeId, categoriaId, tareaId, type, testId } = useParams();
  const navigate = useNavigate();
  const apiType = TYPE_TO_API_PATH[type] ?? type;

  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const [phase, setPhase] = useState(PHASE.LOADING);

  const [seqIndex, setSeqIndex] = useState(0);   // índice en la secuencia principal
  const [digitIndex, setDigitIndex] = useState(0); // índice dentro de los dígitos del trial
  const [userInput, setUserInput] = useState([]);  // dígitos ingresados por el usuario
  const [correctCount, setCorrectCount] = useState(0);
  const [lastCorrect, setLastCorrect] = useState(null); // true/false/null para feedback
  const [showResults, setShowResults] = useState(false);

  const timerRef       = useRef(null);
  const usageLoggedRef = useRef(false);
  const correctCountRef = useRef(0);
  const seqIndexRef     = useRef(0);
  correctCountRef.current = correctCount;
  seqIndexRef.current     = seqIndex;

  useEffect(() => {
    getTestConfig(apiType, testId)
      .then((data) => { setConfig(data); setPhase(data.instrucciones ? PHASE.INSTRUCTIONS : PHASE.SHOWING); })
      .catch((e) => setError(e.message));
  }, [apiType, testId]);

  const goBack = useCallback(() => {
    navigateAfterTest(
      navigate,
      `/lenguaje/${lenguajeId}/categoria/${categoriaId}/tarea/${tareaId}`,
    );
  }, [navigate, lenguajeId, categoriaId, tareaId]);

  useEffect(() => {
    if (phase !== PHASE.DONE || usageLoggedRef.current) return;
    usageLoggedRef.current = true;
    const total = seqIndexRef.current + 1;
    const correctas = correctCountRef.current;
    registrarUso({
      tipo_test:   `DDI${testId}`,
      test_id:     testId,
      tarea_id:    tareaId,
      nombre_test: config?.descripcion ?? '',
      completada:  true,
      total_items: total,
      correctas,
      errores:     total - correctas,
    });
  }, [phase, testId, tareaId, config]);

  const handleExit = useCallback(() => {
    if (!usageLoggedRef.current) {
      usageLoggedRef.current = true;
      const total = seqIndexRef.current + 1;
      const correctas = correctCountRef.current;
      registrarUso({
        tipo_test:   `DDI${testId}`,
        test_id:     testId,
        tarea_id:    tareaId,
        nombre_test: config?.descripcion ?? '',
        completada:  false,
        total_items: total,
        correctas,
        errores:     total - correctas,
      });
    }
    goBack();
  }, [goBack, testId, tareaId, config]);

  const speedMultiplier = useSpeedMultiplier();
  const secuencia = config?.ListaValores?.Secuencia ?? [];
  const tiempoMili = (config?.tiempo_mili ?? 1000) / speedMultiplier;
  const ordenInverso = config?.OrdenInverso ?? false;
  const conclusiones = config?.Conclusiones ?? config?.Concluciones ?? '';

  const currentSeq = secuencia[seqIndex];
  const digits = useMemo(
    () => (currentSeq?.es_consigna ? [] : parseDigits(currentSeq?.Valor)),
    [currentSeq],
  );

  // Mostrar dígitos uno a uno (no aplica a consignas)
  useEffect(() => {
    if (phase !== PHASE.SHOWING || !currentSeq) return;
    if (currentSeq.es_consigna) return;
    if (digitIndex >= digits.length) {
      setPhase(PHASE.BLANK);
      return;
    }
    timerRef.current = setTimeout(() => {
      setDigitIndex((i) => i + 1);
    }, tiempoMili);
    return () => clearTimeout(timerRef.current);
  }, [phase, digitIndex, digits.length, tiempoMili, currentSeq]);

  // Pausa breve antes de mostrar botones de respuesta
  useEffect(() => {
    if (phase !== PHASE.BLANK) return;
    timerRef.current = setTimeout(() => {
      setUserInput([]);
      setPhase(PHASE.RESPONSE);
    }, BLANK_DURATION);
    return () => clearTimeout(timerRef.current);
  }, [phase]);

  const handleDigitClick = useCallback((digit) => {
    setUserInput((prev) => [...prev, digit]);
  }, []);

  const handleDelete = useCallback(() => {
    setUserInput((prev) => prev.slice(0, -1));
  }, []);

  const handleConfirm = useCallback(() => {
    const expected = ordenInverso ? [...digits].reverse() : digits;
    const correct = userInput.length === expected.length &&
      userInput.every((d, i) => d === expected[i]);

    setLastCorrect(correct);

    if (correct) {
      setCorrectCount((c) => c + 1);
      const next = seqIndex + 1;
      if (next >= secuencia.length) {
        setPhase(PHASE.DONE);
      } else {
        setSeqIndex(next);
        setDigitIndex(0);
        setUserInput([]);
        setLastCorrect(null);
        setPhase(PHASE.SHOWING);
      }
    } else {
      setPhase(PHASE.DONE);
    }
  }, [userInput, digits, ordenInverso, seqIndex, secuencia.length]);

  const advanceConsigna = useCallback(() => {
    const next = seqIndex + 1;
    if (next >= secuencia.length) { setPhase(PHASE.DONE); return; }
    setSeqIndex(next);
    setDigitIndex(0);
    setUserInput([]);
  }, [seqIndex, secuencia.length]);

  const handleRetry = () => {
    setSeqIndex(0); setDigitIndex(0); setUserInput([]);
    setCorrectCount(0); setLastCorrect(null); setShowResults(false);
    setPhase(config?.instrucciones ? PHASE.INSTRUCTIONS : PHASE.SHOWING);
  };

  if (error) return <ErrorScreen message={error} onBack={goBack} />;
  if (phase === PHASE.LOADING || !config) return <LoadingScreen />;

  if (phase === PHASE.DONE) {
    const registros = secuencia.slice(0, seqIndex + 1).map((s, i) => {
      const valor = plainValor(s.Valor);
      const expected = ordenInverso ? valor.split(';').reverse().join(';') : valor;
      const wasCorrect = i < correctCount;
      return {
        pregunta:        valor,
        respuesta:       expected,
        respuestaUsuario:'',
        correcta:        wasCorrect,
        tiempoRespuesta: '',
      };
    });

    return (
      <DoneScreen conclusiones={showResults ? null : conclusiones} onRetry={handleRetry} onBack={goBack}>
        <SaveResults tipo={`DDI${testId}`} registros={registros} />
        {showResults ? (
          <>
            <div className="mt-4 bg-slate-50 rounded-lg border border-slate-200 p-6 inline-block">
              <p className="text-4xl font-bold text-slate-800">{correctCount}</p>
              <p className="text-slate-600 text-sm mt-1">
                secuencia{correctCount !== 1 ? 's' : ''} {ordenInverso ? 'inversas' : 'directas'} correcta{correctCount !== 1 ? 's' : ''}
              </p>
            </div>
            <div className="flex justify-center mt-4">
              <button
                onClick={() => setShowResults(false)}
                className="px-4 py-2 rounded-full border border-slate-300 text-slate-700 text-sm bg-white hover:bg-slate-50 cursor-pointer uppercase"
              >
                OCULTAR RESULTADOS
              </button>
            </div>
          </>
        ) : (
          <button
            onClick={() => setShowResults(true)}
            className="px-4 py-2 rounded-full border border-slate-300 text-slate-700 text-sm bg-white hover:bg-slate-50 mt-2 cursor-pointer"
          >
            QUIERO SABER MIS RESULTADOS
          </button>
        )}
      </DoneScreen>
    );
  }

  if (phase === PHASE.INSTRUCTIONS) {
    return (
      <TestLayout current={0} total={0} onExit={goBack}>
        <p className="text-center text-slate-800 whitespace-pre-wrap leading-tight max-w-5xl mb-4" style={{ fontSize: '33px', fontWeight: 700 }}>
          {config.instrucciones}
        </p>
        <ContinueButton onClick={() => setPhase(PHASE.SHOWING)} />
      </TestLayout>
    );
  }

  if (phase === PHASE.SHOWING) {
    if (currentSeq?.es_consigna) {
      return (
        <TestLayout current={seqIndex + 1} total={secuencia.length} onExit={goBack}>
          <SafeHtml
            as="p"
            html={currentSeq.Valor}
            className="text-center text-slate-800 whitespace-pre-wrap leading-tight max-w-5xl mb-4"
            style={{ fontSize: '33px', fontWeight: 700 }}
          />
          <ContinueButton onClick={advanceConsigna} />
        </TestLayout>
      );
    }
    return (
      <TestLayout current={seqIndex + 1} total={secuencia.length} onExit={handleExit}>
        <div className="text-9xl font-bold text-slate-800 min-h-[6rem] flex items-center justify-center tabular-nums">
          {digits[digitIndex] ?? ''}
        </div>
        {ordenInverso && (
          <p className="text-slate-500 text-sm mt-6">Recuerde los dígitos en orden inverso</p>
        )}
      </TestLayout>
    );
  }

  if (phase === PHASE.BLANK) {
    return (
      <TestLayout current={seqIndex + 1} total={secuencia.length} onExit={handleExit}>
        <div className="min-h-[6rem]" />
      </TestLayout>
    );
  }

  // RESPONSE
  return (
    <TestLayout current={seqIndex + 1} total={secuencia.length} onExit={handleExit}>
      <div className="w-full max-w-lg flex flex-col items-center gap-4">
        {ordenInverso && (
          <p className="text-slate-500 text-sm">Ingrese los dígitos en orden inverso</p>
        )}

        {/* Display de la respuesta */}
        <div className="w-full min-h-[4rem] rounded-lg border border-slate-300 bg-slate-50 flex items-center justify-center gap-2 px-4 text-4xl font-mono text-slate-800 font-bold">
          {userInput.length > 0 ? userInput.join('-') : <span className="text-slate-200">—</span>}
        </div>

        {/* Teclado de dígitos */}
        <div className="grid grid-cols-3 gap-3 w-full">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => handleDigitClick(String(d))}
              className="py-4 text-3xl font-semibold rounded-xl border-2 border-slate-300 bg-white hover:bg-violet-50 hover:border-violet-400 text-slate-800 cursor-pointer transition-colors"
            >
              {d}
            </button>
          ))}
          <button
            type="button"
            onClick={handleDelete}
            className="py-4 text-lg font-semibold rounded-xl border-2 border-slate-300 bg-white hover:bg-red-50 hover:border-red-300 text-slate-600 cursor-pointer transition-colors"
          >
            ←
          </button>
          <button
            type="button"
            onClick={() => handleDigitClick('0')}
            className="py-4 text-3xl font-semibold rounded-xl border-2 border-slate-300 bg-white hover:bg-violet-50 hover:border-violet-400 text-slate-800 cursor-pointer transition-colors"
          >
            0
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={userInput.length === 0}
            className="py-4 rounded-xl border-2 border-emerald-400 bg-emerald-50 hover:bg-emerald-100 cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
          >
            <img src="/assets/img/Ok-icon.png" alt="OK" className="h-8 w-auto" />
          </button>
        </div>

        {lastCorrect === false && (
          <p className="text-red-500 text-sm font-medium">Incorrecto — finalizando test</p>
        )}
      </div>
    </TestLayout>
  );
}
